/**
 * Sidebar — builds the scrollable location list with search.
 * Adapted for MapLibre GL JS.
 */

import { getOpenStatus, getAcceptingStatus } from './markers.js';

function escapeHTML(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Build the sidebar list with search box and location entries.
 */
export function buildSidebar(sidebarEl, locations, markers, map, config, clusterManager) {
  // Header (pinned, non-scrolling)
  const header = document.createElement('div');
  header.className = 'pm-sidebar-header';
  sidebarEl.appendChild(header);

  // Search box (in header)
  if (config.search) {
    const searchDiv = document.createElement('div');
    searchDiv.className = 'pm-sidebar-search';

    const input = document.createElement('input');
    input.type = 'search';
    input.placeholder = 'Search locations…';
    input.setAttribute('aria-label', 'Search locations');
    searchDiv.appendChild(input);
    header.appendChild(searchDiv);

    let searchTimeout;
    input.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        filterList(input.value, list, markers);
      }, config.searchDebounce || 200);
    });
  }

  // Filter slot (in header, stays pinned)
  const filterSlot = document.createElement('div');
  filterSlot.className = 'pm-filters-slot';
  header.appendChild(filterSlot);

  // Scrollable body wrapper (only the list scrolls)
  const body = document.createElement('div');
  body.className = 'pm-sidebar-body';
  sidebarEl.appendChild(body);

  // Location list
  const list = document.createElement('ul');
  list.className = 'pm-location-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', 'Locations');

  markers.forEach(({ marker, popup, location, index }) => {
    const item = document.createElement('li');
    item.className = 'pm-location-item';
    item.setAttribute('role', 'option');
    item.setAttribute('data-index', index);

    const btn = document.createElement('button');
    btn.className = 'pm-location-btn';

    const status = getOpenStatus(location);
    const accepting = getAcceptingStatus(location);

    let btnHTML = `<div class="pm-loc-name">${escapeHTML(location.name)}`;

    if (status) {
      const statusClass = status === 'open' ? 'pm-status-open' : 'pm-status-closed';
      const statusText = status === 'open' ? 'Open' : 'Closed';
      btnHTML += ` <span class="pm-status-badge pm-status-badge-sm ${statusClass}">${statusText}</span>`;
    }
    btnHTML += '</div>';

    if (location.address) {
      btnHTML += `<div class="pm-loc-address">${escapeHTML(location.address)}</div>`;
    }

    if (accepting) {
      btnHTML += `<div class="pm-loc-accepting ${accepting.className}"><span class="pm-accepting-icon" aria-hidden="true">${accepting.icon}</span> ${escapeHTML(accepting.text)}</div>`;
    }

    const metaParts = [];
    if (location.specialty) metaParts.push(location.specialty);
    if (location.services) metaParts.push(location.services);
    if (metaParts.length) {
      btnHTML += `<div class="pm-loc-meta">${escapeHTML(metaParts.join(' · '))}</div>`;
    }

    btn.innerHTML = btnHTML;

    // Click handler: close any open popup, fly to marker, open popup
    btn.addEventListener('click', () => {
      list.querySelectorAll('.pm-location-item').forEach(li => li.classList.remove('pm-active'));
      item.classList.add('pm-active');

      if (clusterManager) {
        // Clustered mode: let cluster manager handle zoom + popup
        clusterManager.openLocation(index);
      } else {
        // Non-clustered mode: direct marker control
        markers.forEach(m => {
          if (m.popup && m.popup.isOpen()) m.popup.remove();
        });

        const targetZoom = Math.max(map.getZoom(), 14);

        map.once('moveend', () => {
          marker.togglePopup();
        });

        map.flyTo({
          center: [location.lng, location.lat],
          zoom: targetZoom,
          duration: 800,
        });
      }
    });

    item.appendChild(btn);
    list.appendChild(item);
  });

  body.appendChild(list);
  sidebarEl._pmList = list;
  sidebarEl._pmMarkers = markers;
}

/**
 * Filter the sidebar list by search query.
 */
function filterList(query, listEl, markers) {
  const q = query.toLowerCase().trim();
  let visibleCount = 0;

  listEl.querySelectorAll('.pm-location-item').forEach((item, i) => {
    const loc = markers[i]?.location;
    if (!loc) return;

    const matchesSearch = !q ||
      loc.name.toLowerCase().includes(q) ||
      loc.address.toLowerCase().includes(q) ||
      (loc.specialty && loc.specialty.toLowerCase().includes(q));

    const filterHidden = item.getAttribute('data-filter-hidden') === 'true';
    const visible = matchesSearch && !filterHidden;

    item.setAttribute('aria-hidden', !visible);
    item.style.display = visible ? '' : 'none';
    if (visible) visibleCount++;
  });

  let noResults = listEl.parentNode.querySelector('.pm-no-results');
  if (visibleCount === 0) {
    if (!noResults) {
      noResults = document.createElement('div');
      noResults.className = 'pm-no-results';
      noResults.setAttribute('role', 'status');
      noResults.textContent = 'No locations match your search.';
      listEl.parentNode.insertBefore(noResults, listEl.nextSibling);
    }
    noResults.style.display = '';
  } else if (noResults) {
    noResults.style.display = 'none';
  }
}
